import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { LayoutGrid, FileEdit, Zap, History, Sliders, Mail } from "lucide-react";
import { CommsOverview } from "./Overview";
import { CommsTemplates } from "./Templates";
import { CommsTemplateBuilder } from "./TemplateBuilder";
import { CommsAutomations } from "./Automations";
import { CommsHistory } from "./History";
import { CommsRules } from "./Rules";
import { PendingMessages } from "./PendingMessages";
import { CommsQueue } from "./Queue";
import { ServiceUpdate } from "./ServiceUpdate";

const ADV_KEY = "pout-comms-advanced";

export function StaffCommunications() {
    // Sprint 30: Advanced mode is OFF by default for new users. Only remembered
    // per-browser once someone flips it on.
    const [advanced, setAdvanced] = useState(() => localStorage.getItem(ADV_KEY) === "1");
    const location = useLocation();
    useEffect(() => { localStorage.setItem(ADV_KEY, advanced ? "1" : "0"); }, [advanced]);

    const TABS = useMemo(() => {
        const base = [
            {to: "/staff/communications/service-updates", label: "Service updates", icon: Mail, id: "service-updates"},
            {to: "/staff/communications/queue", label: "Messages to send", icon: Mail, id: "pending"},
            { to: "/staff/communications",             end: true, label: "Overview",    icon: LayoutGrid,   id: "overview" },
            { to: "/staff/communications/automations",            label: "Automations", icon: Zap,          id: "automations" },
            { to: "/staff/communications/templates",              label: "Templates",   icon: FileEdit,     id: "templates" },
            { to: "/staff/communications/history",                label: "History",     icon: History,      id: "history" },
        ];
        if (advanced) {
            base.push({ to: "/staff/communications/rules", label: "Rules (adv.)", icon: Sliders, id: "rules-adv" });
            base.push({ to: "/staff/communications/manage-queue", label: "Queue (adv.)", icon: Sliders, id: "queue-adv" });
        }
        return base;
    }, [advanced]);

    return (
        <div className="space-y-6" data-testid="staff-communications">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-2">
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">Communications</div>
                    <h1 className="font-serif font-light text-4xl text-white leading-[1.05]">Client communications</h1>
                    <p className="text-sm text-white/60 max-w-2xl">
                        Manage all client emails, reminders and automated messages from one place.
                    </p>
                </div>
                <label className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1 shrink-0 cursor-pointer" data-testid="comms-advanced-toggle">
                    <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} className="accent-gold h-4 w-4" />
                    Advanced mode
                </label>
            </header>

            <nav className="flex flex-wrap gap-2 border-b hairline pb-2" data-testid="comms-subnav">
                {TABS.map((t) => (
                    <NavLink
                        key={t.id}
                        to={t.to}
                        end={t.end}
                        data-testid={`comms-tab-${t.id}`}
                        className={({ isActive }) =>
                            `inline-flex items-center gap-2 px-4 h-9 rounded-full text-xs uppercase tracking-[0.18em] border transition-colors ${
                                isActive
                                    ? "bg-gold/15 border-gold/40 text-gold"
                                    : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
                            }`
                        }
                    >
                        <t.icon className="h-3.5 w-3.5" strokeWidth={1.5} /> {t.label}
                    </NavLink>
                ))}
            </nav>

            <Routes>
                <Route path="service-updates" element={<ServiceUpdate />} />
                <Route index element={<CommsOverview />} />
                <Route path="templates" element={<CommsTemplates />} />
                <Route path="templates/:id" element={<CommsTemplateBuilder />} />
                <Route path="automations" element={<CommsAutomations />} />
                <Route path="history" element={<CommsHistory />} />
                {advanced && <Route path="rules" element={<CommsRules />} />}
                <Route path="queue" element={<PendingMessages />} />
                {advanced && <Route path="manage-queue" element={<CommsQueue />} />}
                <Route path="*" element={<Navigate to="." replace />} />
            </Routes>
        </div>
    );
}

export { STATUS_STYLES } from "./shared";
